import { appById, type DesktopAppId } from "./apps";
import { supportsMultipleWindows, windowId, type DesktopWindowState } from "./layout";
import { validateWindowContext, type WorkbenchWindow } from "../lib/workbench";
import { clampWindowBounds, initialWindowBounds, type WorkArea } from "./windowGeometry";

export type DesktopDesk = { id: string; name: string };
export type DeskWindowSnapshot = WorkbenchWindow;
export const defaultDesk: DesktopDesk = { id: "main", name: "Principal" };
export const maximumDesks = 12;
export const deskStorageKey = (userId: string) => `base-sul-os:desks:v1:${userId}`;
export const windowDeskId = (item: DesktopWindowState) => item.deskId ?? defaultDesk.id;
export const hasDeskControlCharacters = (value: string) => Array.from(value).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);

export function readDesks(userId: string, storage?: Pick<Storage, "getItem">): DesktopDesk[] {
  try {
    if (!userId) return [{ ...defaultDesk }];
    const raw: unknown = JSON.parse((storage ?? window.localStorage).getItem(deskStorageKey(userId)) ?? "null");
    if (!raw || typeof raw !== "object" || !("version" in raw) || raw.version !== 1 || !("desks" in raw) || !Array.isArray(raw.desks)) return [{ ...defaultDesk }];
    const result: DesktopDesk[] = [{ ...defaultDesk }];
    const seen = new Set([defaultDesk.id]);
    for (const item of raw.desks.slice(0, maximumDesks)) {
      if (!item || typeof item !== "object" || typeof item.id !== "string" || !/^[a-zA-Z0-9-]{1,80}$/.test(item.id) || seen.has(item.id) || typeof item.name !== "string") continue;
      const rawName: string = item.name;
      const name = Array.from(rawName.trim()).filter((character) => !hasDeskControlCharacters(character)).join("").slice(0, 48);
      if (!name) continue;
      seen.add(item.id);
      result.push({ id: item.id, name });
      if (result.length === maximumDesks) break;
    }
    return result;
  } catch { return [{ ...defaultDesk }]; }
}

export function saveDesks(userId: string, desks: DesktopDesk[], storage?: Pick<Storage, "setItem">): boolean {
  if (!userId) return false;
  try { (storage ?? window.localStorage).setItem(deskStorageKey(userId), JSON.stringify({ version: 1, desks: desks.slice(0, maximumDesks) })); return true; }
  catch { return false; }
}

export function captureDeskWindows(windows: DesktopWindowState[], area: WorkArea, includeContext = false): DeskWindowSnapshot[] {
  return windows.slice(0, 48).map((item) => {
    const bounds = item.snap ? { x: item.snap === "left" ? 0 : area.width / 2, y: 0, width: area.width / 2, height: area.height } : item.bounds ?? initialWindowBounds(area);
    return { appId: item.appId, ...(item.instanceId ? { instanceId: item.instanceId } : {}), ...(includeContext && validateWindowContext(item.appId, item.context) ? { context: item.context } : {}), x: Math.round(bounds.x), y: Math.round(bounds.y), width: Math.round(bounds.width), height: Math.round(bounds.height), maximized: item.maximized, minimized: item.minimized };
  });
}

/** These apps may attach to an existing session or trigger external network activity. Never launch them from a snapshot. */
const explicitLaunchApps = new Set<DesktopAppId>(["host-browser", "host-terminal", "host-codex", "crm"]);

export function restoreDeskWindows(current: DesktopWindowState[], source: unknown, allowed: Set<DesktopAppId>, area: WorkArea, deskId: string): { windows: DesktopWindowState[]; skipped: string[]; restored: number } {
  if (!Array.isArray(source) || source.length > 48) throw new Error("A disposição salva não é válida.");
  const windows = [...current];
  const seen = new Set<string>();
  const skipped: string[] = [];
  let restored = 0;
  const topZ = Math.max(0, ...current.map((item) => item.z));
  for (const item of source) {
    if (!item || typeof item !== "object" || typeof item.appId !== "string") continue;
    const instanceId = typeof item.instanceId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(item.instanceId) ? item.instanceId : undefined;
    const app = appById(item.appId as DesktopAppId);
    if (!app || !allowed.has(app.id)) continue;
    const multi = supportsMultipleWindows(app.id);
    const key = multi ? instanceId ?? app.id : app.id;
    if (seen.has(key)) continue;
    seen.add(key);
    const index = windows.findIndex((existing) => existing.appId === app.id && (!multi || (windowId(existing) === key && windowDeskId(existing) === deskId)));
    if (index < 0 && explicitLaunchApps.has(app.id)) { skipped.push(app.title); continue; }
    if (index < 0 && windows.length >= 48) { skipped.push(app.title); continue; }
    if (![item.x, item.y, item.width, item.height].every((value) => typeof value === "number" && Number.isFinite(value)) || item.width <= 0 || item.height <= 0) continue;
    const bounds = clampWindowBounds({ x: item.x, y: item.y, width: item.width, height: item.height }, area);
    const next: DesktopWindowState = {
      appId: app.id, deskId, bounds, minimized: item.minimized === true,
      maximized: item.maximized === true, snap: null, z: topZ + restored + 1,
      ...(index < 0 && multi ? { instanceId: instanceId && !windows.some((existing) => windowId(existing) === instanceId) ? instanceId : windows.some((existing) => existing.appId === app.id) ? crypto.randomUUID() : undefined } : {}),
      // Existing components keep live drafts and navigation. Explicit restore
      // initializes context only in new instances, never overwrites live state.
      ...(index < 0 && validateWindowContext(app.id, item.context) ? { context: item.context } : {}),
    };
    if (index < 0) windows.push(next); else windows[index] = { ...windows[index], ...next };
    restored += 1;
  }
  return { windows, skipped, restored };
}
